// Summary of this file:
// Everything related to game pcode & game name, but not betting panel.

import { IGameObjectType, IKeyStringDict } from "@/types/GlobalTypes";
import { isNullOrEmpty } from "./commonMobile";

/**
 * Used in socket events
 */
const gamenames: IKeyStringDict = {
	"12D": "p9",
	"24D": "p6",
	"24DSPIN": "m6",
	"36D": "m36",
	"3D": "m31",
	"48D": "m35",
	"5D": "m30",
	"BC": "m22",
	"BILLIARDS": "m13",
	"BINGOSICBO": "m37",
	"BL": "m13",
	"BM": "m38",
	"D6": "m8",
	"DMN": "m41",
	"DT": "m23",
	"FT": "m25",
	"GB": "m16",
	"GBJ": "m33",
	"GONGBALLJITU": "m33",
	"HILO": "m34",
	"HNT": "m10",
	"IFS": "m28",
	"LG": "m26",
	"MP": "m20",
	"MPW": "m21",
	"NN": "m24",
	"OG": "m7",
	"PB": "m42",
	"PD": "m14",
	"RL": "p7",
	"RW": "m11",
	"SB": "m29",
	"SD": "p12",
	"SF": "m27",
	"SG": "m32",
	"SW": "m19",
	"TOTOMACAU": "m17",
	"XD": "m40",
	"DUELDICE": "m39"
};

/**
 * This is favoured for consistency
 * Used in socket event "loadNewValue"
 */
const condensedGameNames: IKeyStringDict = {
	"12d": "p9",
	"24d": "p6",
	"24dspin": "m6",
	"36d": "m36",
	"3d": "m31",
	"48d": "m35",
	"5d": "m30",
	"baccarat": "m22",
	"baccaratmachine": "m38",
	"billiards": "m13",
	"bingosicbo": "m37",
	"dice6": "m8",
	"domino": "m41",
	"dueldice": "m39",
	"dragontiger": "m23",
	"fantan": "m25",
	"gongball": "m16",
	"gongballjitu": "m33",
	"headtail": "m10",
	"hilo": "m34",
	"idn4stand": "m28",
	"lotto": "m26",
	"monopoly": "m20",
	"monopolyworldcup": "m21",
	"niuniu": "m24",
	"oglok": "m7",
	"pinballs": "m42",
	"pokerdice": "m14",
	"raceball": "m26",
	"redwhite": "m11",
	"roulette": "p7",
	"samgong": "m32",
	"shioballs": "m29",
	"shiofights": "m27",
	"sicbodice": "p12",
	"sicbomachine": "m31",
	"suwit": "m19",
	"totomacau": "m17",
	"totomacau5d": "m51",
	"totosingapore": "m50",
	"xocdia": "m40"
};

/**
 * pcode to capital gamecode conversion list
 */
const gamecodes: IKeyStringDict = {
	"m10": "HNT",
	"m11": "RW",
	"m13": "BL",
	"m14": "PD",
	"m16": "GB",
	"m19": "SW",
	"m20": "MP",
	"m21": "MPW",
	"m22": "BC",
	"m23": "DT",
	"m24": "NN",
	"m25": "FT",
	"m26": "LG",
	"m27": "SF",
	"m28": "IFS",
	"m29": "SB",
	"m30": "5D",
	"m31": "3D",
	"m32": "SG",
	"m33": "GBJ",
	"m34": "HILO",
	"m35": "48D",
	"m36": "36D",
	"m37": "BINGOSICBO",
	"m38": "BM",
	"m40": "XD",
	"m41": "DMN",
	"m42": "PB",
	"m6": "24DSPIN",
	"m7": "OG",
	"m8": "D6",
	"p12": "SD",
	"p6": "24D",
	"p7": "RL",
	"p9": "12D",
	"m39": "DUELDICE"
};

/**
 * Use this just in case there's no language string yet
 */
const defaultGameNames: IKeyStringDict = {
	"m10": "Head Tail",
	"m11": "Red White",
	"m13": "Billiards",
	"m14": "Poker Dice",
	"m16": "Gong Ball",
	"m19": "Suwit",
	"m20": "Monopoly",
	"m21": "Monopoly World Cup",
	"m22": "Baccarat",
	"m22b": "Baccarat B",
	"m22c": "Baccarat C",
	"m23": "Dragon Tiger",
	"m24": "NiuNiu",
	"m25": "FanTan",
	"m26": "Lotto",
	"m27": "Shio Fights",
	"m28": "IDN 4 Stand",
	"m28b": "IDN 4 Stand B",
	"m29": "Shio Balls",
	"m30": "5D",
	"m31": "3D",
	"m32": "SamGong",
	"m33": "Gong Ball Jitu",
	"m37": "Bingo Sicbo",
	"m38": "Bingo Baccarat",
	"m38b": "Bingo Baccarat 2",
	"m39": "Duel Dice",
	"m40": "Xoc Dia",
	"m41": "Domino",
	"m42": "Pinballs",
	"m50": "Toto Singapore",
	"m6": "24Dspin",
	"m7": "Oglok",
	"m8": "Dice 6",
	"p12": "Sicbo[Dice]",
	"p6": "24D",
	"p7": "Roulette",
	"p9": "12D"
};

const
	v1games = [
		"m6", "m7", "m8", "m10",
		"m11", "m13", "m14", "m16",
		"m19", "m20", "m31", "m32",
		"m35", "m36", "m37",
		"p6", "p7", "p9", "p12"
	],
	v2games = [
		"m22", "m23", "m27", "m28",
		"m34", "m38"
	],
	v4games = [
		"m24", "m25"
	],
	totoGames = [
		"m17", "m50", "m51"
	],
	machineGames = [
		"m26", "m29", "m30", "m42"
	];


const
	isPcode = (str: string) =>
		str && str.match(/^[mp]\d+[a-z]?$/),

	isV1Game = (pcode: string) =>
		v1games.includes(getBasePcode(pcode)),

	isV2Game = (pcode: string) =>
		v2games.includes(getBasePcode(pcode)),

	isV4Game = (pcode: string) =>
		v4games.includes(getBasePcode(pcode)),

	isTotoGame = (pcode: string) =>
		totoGames.includes(getBasePcode(pcode));


const gameIsVariant = (pcode: string) =>
	!!pcode.match(/[a-z]$/);

const isMachineGame = (pcode: string) =>
	machineGames.includes(getBasePcode(pcode));

/**
 * Obtain existing game designs if the game is the same.
 * Example: m28 takes m22 as the base design
 * Output format: [descendant]: return [parent]
 * @param basePcode Any base pcode (hint: use getBasePcode)
 */
function getClonePcode(basePcode: string) {
	switch (basePcode) {
		case "m6": return "p6";
		case "m21": return "m20";
		case "m28": return "m22";

		// Baccarat Medium & Baccarat Small
		case "m38": return "m22";
		case "m31": return "p12";
		case "m37": return "p12";

		// case "m50": return "m17";
		case "m51": return "m17";

		default: return basePcode;
	}
}

/**
 * Get pcode that is original (not added with letters behind).
 * @param mixedPcode pcode that is about to be trimmed
 */
function getBasePcode(mixedPcode: string) {
	if (!mixedPcode || !mixedPcode.trim())
		throw new Error("mixedPcode is empty!");

	return mixedPcode.match(/[mp]\d+/) + "";
}

/**
 * 
 * @param code either "toto" or pcode
 */
function isTotoGroup(code: string) {
	return code === "toto";
}

/**
 * Toto game grouping problem helper
 * for use in Desktop index & Mobile Lobby
 * @returns "toto" or basePcode
 */
function getGroupCode(pcode: string) {
	const basePcode = getBasePcode(pcode);

	if (isTotoGame(basePcode))
		return "toto";

	return basePcode;
}

function getAvailableTotoPcodes(pcodes: string[]) {
	return pcodes.filter(pcode =>
		isTotoGame(getBasePcode(pcode))
	);
}

function getFirstTotoPcode(pcodes: string[]) {
	const filtered = getAvailableTotoPcodes(pcodes);
	return filtered.length > 0 ? filtered[0] : "";
}

/**
 * Used in fetchGameList()
 * @returns Unique base pcodes
 */
function getBasePcodes(pcodes: string[]) {
	return [...new Set(
		pcodes.map(pcode => getBasePcode(pcode))
	)];
}

/**
 * for use together with state.gameGroups
 * @param pcodes 
 * @returns 
 */
function getGroupSize(pcodes: string[]) {
	return pcodes ? pcodes.length : 0;
}

/**
 * for use in Desktop & Mobile Lobby, especially when filtering games
 * @returns an array of pcodes
 */
function getPcodesByCategory(games: IGameObjectType, category: string) {
	return Object.keys(games)
		.filter(pcode => games[pcode].category === category);
}


/**
 * For use in fetchGameList()
 * Turn an array of pcodes to a dictionary
 */
function getGroupDict(pcodes: string[]) {
	const dict = {};

	for (const pcode of pcodes) {
		const
			basePcode = getBasePcode(pcode),
			groupCode = getGroupCode(basePcode);

		if (dict[groupCode])
			dict[groupCode].push(pcode);
		else dict[groupCode] = [pcode];
	}

	return dict;
}

/**
 * Get the letter variant of the main game.
 * Returns only the letter of the pcode.
 */
function getGameVariantLetter(pcode: string): string {
	if (!pcode) {
		console.error("gGVL pcode is empty");
		return "";
	}

	//Enable for numbers
	return (pcode === "m22") ? "1" :
		isNaN(+pcode[pcode.length - 1]) ?
			pcode[pcode.length - 1].charCodeAt(0) - 96 + "" : "";

	//Enable for letters
	// return (pcode === "m22") ? "A" : isNaN(pcode[pcode.length - 1]) ? pcode[pcode.length - 1].toUpperCase() : "";
}

//Summary:
// Gets game variants with the same basePcode
//Example:
// Baccarat A, B and C
// m22, m22b, m22c
//Parameters:
// gameArray: object
// pcode: string
const getGameVariants = (games: IGameObjectType, basePcode: string) =>
	Object.keys(games)
		.filter(pcode => pcode.includes(basePcode));


/**
 * This function will guess whether the game is in gamename or condensedGameNames or is plain pcode.
 * @param {IGameType|string} data The game object/game name string to process.
 */
function newGetPcode(data: any) { // { game?: string, pcode?: string} | string
	if (isNullOrEmpty(data) ||
		(Number(data + "")) === 0)
		return null;

	const stringToProcess = data.game || data.pcode || data;

	if (!stringToProcess.split)
		return null;

	// Checks data if it's already pcode
	if (isPcode(stringToProcess))
		return stringToProcess;

	if (data.pcode && isPcode(data.pcode))
		return data.pcode;


	const possibleVariants = ["b", "c"];


	// Gets last letter as variant
	const lastLetter = stringToProcess.substr(-1);
	let variantLetter = "";
	if (isNaN(Number(lastLetter)) &&
		possibleVariants.indexOf(lastLetter.toLowerCase()) >= 0)
		variantLetter = lastLetter.toLowerCase();


	// Checks against game name with capital letters
	if (stringToProcess in gamenames)
		return gamenames[stringToProcess];

	const hasVariantLetter = variantLetter !== "";
	const trimmedGameName = hasVariantLetter ?
		stringToProcess.substring(0, stringToProcess.length - 1) :
		stringToProcess;

	if (trimmedGameName in gamenames)
		return gamenames[trimmedGameName] + variantLetter;


	// Check against condensed game name list
	const condensedGameName = trimmedGameName.replace(/(_|-| |\[|\])/g, "");
	return condensedGameNames[condensedGameName] + variantLetter;
}

/**
 * Gets capital gamecode.
 * See const gamecodes for reference.
 * @param pcode Any game pcode.
 * @returns {string} The fully capital gamecode, refer to gamecodes const.
 */
function getGameCode(pcode: string): string {
	const basePcode = getBasePcode(pcode);
	const lastLetter = !isNaN(+pcode[pcode.length - 1]) ? "" :
		pcode[pcode.length - 1];

	return (basePcode in gamecodes) ?
		gamecodes[basePcode] + lastLetter.toUpperCase() :
		"unknown game: pcode " + pcode;
}

//Checks for the first non-empty pcode just in case
//  there are multiple pcodes in the game stack.
//Game stack is the object fetched from fetchGameList()
//Example:
//{ "p7b": { game content }, "p7": { game content } }
//What is requested: p7b
//Take p7 instead of p7b
function getFirstNotEmptyPcode(games: IGameObjectType, pcode: string): string {
	const basePcode = getBasePcode(pcode);

	for (const p in games)
		if (getBasePcode(p) === basePcode)
			return p;

	return "";
}


//This is applied to:
//- lobby header
//- game header for V1
//- mobile lobby
//- mobile portrait & landscape header for V1

//Summary: Returns cloned game pcode as default
// and may differentiate between similar games, for example 24D and 24DSpin
// 24DSpin has its own icon while using the betting panel from 24D

// Note: As of 02-12-2019, assets have been renamed to pcodes to avoid ambiguity from database naming inconsistency
function getAssetPcode(clonePcode: string): string {
	const basePcode = getBasePcode(clonePcode);

	// format: { descendant: parent }
	const sameAsset = {
		"m33": "m16",
		// "m35": "p6",
		"m36": "p7",

		"m50": "m17",
		"m51": "m17"
	};

	const ownAssets = ["m6", "m31", "m50"];

	return (basePcode in sameAsset) ?
		sameAsset[basePcode] :

		ownAssets.includes(basePcode) ?
			basePcode :

			getClonePcode(basePcode);
}

export {
	defaultGameNames,
	v1games,
	v2games,
	v4games,
	totoGames,
	machineGames,
	isMachineGame,

	isPcode,
	isV1Game,
	isV2Game,
	isV4Game,
	isTotoGame,
	gameIsVariant,

	getClonePcode,
	getBasePcode,

	isTotoGroup,
	getGroupCode,
	getAvailableTotoPcodes,
	getFirstTotoPcode,

	getBasePcodes,
	getGroupDict,
	getGroupSize,
	getPcodesByCategory,

	getGameVariantLetter,
	getGameVariants,
	newGetPcode,
	getGameCode,
	getFirstNotEmptyPcode,
	getAssetPcode
}